import { DateService } from '@rocheglobal/component-library-dot-com/src/services/date';
import React, { createRef, MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import { PageItem, PageItems } from '../../services/teaser/types';
import { LanguageService } from '../../services/language';
import { TeaserService } from '../../services/teaser';
import { ConditionalWrapper } from '../../helpers/conditional-wrapper';
import { parseAssetSource } from '../../utils/asset';
import { BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { RocheTeaser as RocheTeaserType } from '../../types/global';
import { RocheUITeaser, RocheUITeaserLayout } from '@repo/roche-ui';
import { useTranslation } from '../../hooks/use-translation';
import { StringService } from '../../services/string';
import { handleRocheTeaserClick } from '../../helpers/roche-teaser-tracking';
import { getAltText } from './alt-text-helper';

type RocheTeaserAutoProps = BlokToComponentProps<
  RocheTeaserType & {
    page: undefined | number;
    group_tags: string[];
    sorting: string;
    tags: string[];
    teaser_number: number;
  }
>;

interface RocheTeaserAutoState {
  teasers: PageItems;
}

const initialState: RocheTeaserAutoState = {
  teasers: {} as PageItems,
};

export const RocheTeaserAuto = ({ blok, updateHoistedState, slot, wrapperTag }: RocheTeaserAutoProps): JSX.Element => {
  const [state, setState] = useState<RocheTeaserAutoState>(initialState);
  const Wrapper = wrapperTag as React.ElementType;
  const readMoreTranslation = useTranslation('read more');
  const teaserRefs = useRef<RefObject<HTMLDivElement>[]>([]);

  useEffect(() => {
    const { group_tags: groupTags, page, sorting, tags, teaser_number: number } = blok;

    if (!sorting || !number || [...tags, ...groupTags].length <= 0) {
      return;
    }

    TeaserService.getTeasers(tags, groupTags, sorting, page || 1, number).then(async (result) => {
      const itemsWithDate = await Promise.all(
        result.items.map(async (teaser: PageItem) => ({
          ...teaser,
          first_published_at: await DateService.formatDate(teaser?.first_published_at as string | Date, 'dateFull'),
        })),
      );

      teaserRefs.current = result.items.map((_, i) => teaserRefs.current[i] ?? createRef());

      setState({
        teasers: {
          ...result,
          items: itemsWithDate,
        },
      });
      return updateHoistedState?.(result.items.length, result.total, `${tags}${groupTags}`, result);
    });
  }, []);

  if (Object.keys(state.teasers).length <= 0) {
    return null;
  }

  return (
    <>
      {state.teasers.items.map((teaser: PageItem, index: number) => (
        <ConditionalWrapper
          key={index}
          condition={wrapperTag !== undefined && wrapperTag !== ''}
          wrapper={(children: JSX.Element): JSX.Element => <Wrapper slot={slot}>{children}</Wrapper>}
        >
          <RocheUITeaser
            ref={teaserRefs.current[index]}
            slot={slot}
            key={index}
            loaded={teaser.content !== undefined}
            layout={blok.layout}
            teaserImage={parseAssetSource(teaser.content?.teaser_image[0]?.src.filename)}
            teaserImageAlt={getAltText(teaser.content?.teaser_image[0])}
            teaserTitle={teaser.content?.teaser_title}
            teaserOverline={
              RocheUITeaserLayout.VARIANT_C_WITHOUT_IMAGE_WITH_DATE === blok.layout
                ? teaser?.first_published_at
                : teaser.content?.teaser_overline
            }
            primaryButtonText={StringService.capitalize(readMoreTranslation)}
            teaserDescription={teaser.content?.teaser_description}
            teaserUrl={LanguageService.getLocaleAwareLink(
              teaser?.default_full_slug,
              LanguageService.getParsedLocale(LanguageService.getActiveLanguage()),
            )}
            onTeaserClick={(e: MouseEvent) =>
              handleRocheTeaserClick(
                e,
                teaser.content?.teaser_title,
                LanguageService.getLocaleAwareLink(
                  teaser?.default_full_slug,
                  LanguageService.getParsedLocale(LanguageService.getActiveLanguage()),
                ),
                teaserRefs.current[index],
              )
            }
          />
        </ConditionalWrapper>
      ))}
    </>
  );
};
